import React from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useEditTask } from "@planzone/view-models";
import { DateTimePicker } from "@common/dateTimePicker";
import { useWorkspaceContext } from "@scenes/workspaceContext";
import { Props } from "./types";
import styles from "./styles.scss";

function DatesPickers({ task, error, readOnly }: Props) {
  const { t } = useTranslation();
  const workspace = useWorkspaceContext();
  const projectTimezone =
    workspace.type === "project" ? workspace.project.timeZone : undefined;
  // Same fallback as @common/dateTimePicker, so day/hour comparisons below
  // always happen in the zone the picker actually displays and commits in.
  const zone = projectTimezone || DateTime.local().zoneName;
  const toZonedDate = (iso: string) =>
    DateTime.fromISO(iso, { zone: "utc" }).setZone(zone);
  const toCalendarDate = (iso: string) => {
    const date = toZonedDate(iso);
    return { year: date.year, month: date.month - 1, day: date.day };
  };
  const [startDate, setStartDate] = React.useState(task.startDate);
  const [endDate, setEndDate] = React.useState(task.endDate);
  const [completionDate, setCompletionDate] = React.useState(
    task.completionDate,
  );
  const start = startDate
    ? toZonedDate(startDate)
    : DateTime.now().setZone(zone);
  const end = endDate ? toZonedDate(endDate) : DateTime.now().setZone(zone);
  const completion = completionDate
    ? toZonedDate(completionDate)
    : DateTime.now().setZone(zone);
  const { editTask } = useEditTask(task);

  React.useEffect(() => {
    setStartDate(task.startDate);
  }, [task.startDate]);

  React.useEffect(() => {
    setEndDate(task.endDate);
  }, [task.endDate]);

  React.useEffect(() => {
    setCompletionDate(task.completionDate);
  }, [task.completionDate]);

  return (
    <div className={styles.datesWrapper}>
      <div className={styles.group}>
        <h3 className={styles.title}>{t("taskRetrieval.startDate")}</h3>
        <div className={styles.input}>
          <DateTimePicker
            projectTimezone={projectTimezone}
            dateTime={startDate}
            placeholder={t("taskRetrieval.startDate")}
            isInvalid={error.startDateOutOfBound || error.invalidDateRange}
            readOnly={readOnly}
            minDate={
              task.minStartDate ? toCalendarDate(task.minStartDate) : undefined
            }
            maxDate={(() => {
              const bound = endDate || task.maxEndDate;
              return bound ? toCalendarDate(bound) : undefined;
            })()}
            input={(date) => {
              const nextDate = toZonedDate(date);

              if (
                start.day !== nextDate.day ||
                start.hour !== nextDate.hour ||
                start.minute !== nextDate.minute
              ) {
                const adjustedEnd =
                  endDate && nextDate.hasSame(end, "day") && nextDate >= end
                    ? (nextDate.plus({ hours: 1 }).toUTC().toISO() ?? endDate)
                    : endDate;

                editTask({
                  task,
                  nextTask: {
                    ...task,
                    startDate: date,
                    endDate: adjustedEnd,
                  },
                });
              } else {
                setStartDate(date);
              }
            }}
            clear={() =>
              editTask({
                task,
                nextTask: { ...task, startDate: "" },
              })
            }
            today={() => {
              const now = new Date();
              editTask({
                task,
                nextTask: { ...task, startDate: now.toISOString() },
              });
            }}
          />
        </div>
      </div>
      <div className={styles.group}>
        <h3 className={styles.title}>{t("taskRetrieval.endDate")}</h3>
        <div className={styles.input}>
          <DateTimePicker
            projectTimezone={projectTimezone}
            dateTime={endDate}
            placeholder={t("taskRetrieval.endDate")}
            isInvalid={error.startDateOutOfBound || error.invalidDateRange}
            readOnly={readOnly}
            minDate={(() => {
              const bound = startDate || task.minStartDate;
              return bound ? toCalendarDate(bound) : undefined;
            })()}
            input={(date) => {
              const nextDate = toZonedDate(date);

              if (
                end.day !== nextDate.day ||
                end.hour !== nextDate.hour ||
                end.minute !== nextDate.minute
              ) {
                const adjustedStart =
                  startDate &&
                  nextDate.hasSame(start, "day") &&
                  nextDate <= start
                    ? date
                    : startDate;

                editTask({
                  task,
                  nextTask: {
                    ...task,
                    startDate: adjustedStart,
                    endDate: date,
                  },
                });
              } else {
                setEndDate(date);
              }
            }}
            clear={() =>
              editTask({
                task,
                nextTask: { ...task, endDate: "" },
              })
            }
            today={() => {
              const now = new Date();
              editTask({
                task,
                nextTask: { ...task, endDate: now.toISOString() },
              });
            }}
          />
        </div>
      </div>
      {!!task.completionDate && (
        <div className={styles.group}>
          <h3 className={`${styles.title} ${styles.completionTitle}`}>
            {t("taskRetrieval.completionDate")}
          </h3>
          <div className={`${styles.input} ${styles.completionInput}`}>
            <DateTimePicker
              projectTimezone={projectTimezone}
              dateTime={completionDate}
              placeholder={t("taskRetrieval.completionDate")}
              isInvalid={false}
              readOnly={readOnly}
              input={(date) => {
                const nextDate = toZonedDate(date);

                completion.day !== nextDate.day ||
                completion.hour !== nextDate.hour ||
                completion.minute !== nextDate.minute
                  ? editTask({
                      task,
                      nextTask: {
                        ...task,
                        completionDate: date,
                      },
                    })
                  : setCompletionDate(date);
              }}
              today={() => {
                const now = new Date();
                editTask({
                  task,
                  nextTask: { ...task, completionDate: now.toISOString() },
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export { DatesPickers };
